import { useEffect, useId, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { Users, Check, Loader2 } from "lucide-react";

/**
 * Autocomplete client réutilisable avec recherche **serveur** (debounce 250ms).
 * - Pas de Radix Popover (évite role=button propagé à l'Input).
 * - A11y : role=combobox sur l'Input + role=listbox / option sur la liste.
 * - Scale : fonctionne avec des tenants > 5000 clients (backend limite + index).
 */
export const ClientAutocomplete = ({
  value,
  onChange,
  onPickClient,
  placeholder = "Nom du client",
  testId = "order-client-input",
  required = false,
  includeImported = true,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const blurTimeoutRef = useRef(null);
  const reqIdRef = useRef(0);
  const listboxId = useId();

  // Recherche serveur avec debounce 250ms
  useEffect(() => {
    const q = (value || "").trim();
    if (!q) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    const reqId = ++reqIdRef.current;
    setLoading(true);
    const timer = setTimeout(() => {
      api
        .getClients({ search: q, include_imported: includeImported, limit: 10 })
        .then((r) => {
          if (reqId !== reqIdRef.current) return; // stale
          setSuggestions((r?.data?.items || []).slice(0, 10));
        })
        .catch(() => {
          if (reqId !== reqIdRef.current) return;
          setSuggestions([]);
        })
        .finally(() => {
          if (reqId === reqIdRef.current) setLoading(false);
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [value, includeImported]);

  useEffect(() => () => { if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current); }, []);

  const pick = (client) => {
    onChange(client.client_name || "");
    const rawCity = client.profile_city || client.city || "";
    const cleanCity = rawCity.replace(/\s*\(\d{4,5}\)\s*$/, "").trim();
    onPickClient?.({
      client_name: client.client_name,
      client_email: client.client_email,
      phone: client.phone,
      address_line1: client.address_line1,
      postal_code: client.postal_code,
      city: cleanCity,
    });
    setOpen(false);
    setActiveIdx(-1);
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      pick(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIdx(-1);
    }
  };

  const showList = open && (value || "").trim().length > 0 && (loading || suggestions.length > 0);

  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActiveIdx(-1); }}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        data-testid={testId}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listboxId}
        aria-activedescendant={activeIdx >= 0 && suggestions[activeIdx] ? `${listboxId}-opt-${activeIdx}` : undefined}
      />
      {showList && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-[60] left-0 right-0 mt-1 rounded-md border bg-popover shadow-md py-1 max-h-64 overflow-y-auto"
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
            {loading ? (
              <><Loader2 className="h-3 w-3 animate-spin" /> Recherche…</>
            ) : (
              <><Users className="h-3 w-3" /> {suggestions.length} client(s)</>
            )}
          </div>
          {!loading && suggestions.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">Aucun résultat.</div>
          )}
          {suggestions.map((c, idx) => (
            <button
              key={c.key}
              type="button"
              id={`${listboxId}-opt-${idx}`}
              role="option"
              aria-selected={idx === activeIdx}
              onClick={() => pick(c)}
              onMouseEnter={() => setActiveIdx(idx)}
              className={
                "w-full text-left px-3 py-2 text-sm flex items-start justify-between gap-2 group " +
                (idx === activeIdx ? "bg-slate-100" : "hover:bg-slate-100")
              }
              data-testid={`order-client-suggestion-${c.key}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{c.client_name || "—"}</div>
                {c.client_email && (
                  <div className="text-xs text-muted-foreground truncate">{c.client_email}</div>
                )}
                {(c.profile_city || c.postal_code) && (
                  <div className="text-xs text-muted-foreground truncate">
                    {c.profile_city} {c.postal_code ? `(${c.postal_code})` : ""}
                  </div>
                )}
              </div>
              <Check className="h-3.5 w-3.5 mt-1 text-primary opacity-0 group-hover:opacity-100" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientAutocomplete;
